import { useEffect, useRef, useState } from "react";

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const EMAILJS_SERVICE_ID  = "service_phjsyq5";
const EMAILJS_TEMPLATE_ID = "template_0tyg2jk";
const EMAILJS_PUBLIC_KEY  = "bRmPXALtqcHX4nz4a";
const CALENDLY_LINK       = "https://calendly.com/sarisater/new-meeting";
const API_URL             = "/api/analyze";

const SCAN_LINES = [
  "Fetching the page source and raw content.",
  "Auditing title tags, meta descriptions, and heading hierarchy.",
  "Evaluating AI search signals — ChatGPT, Perplexity, and AI Overviews.",
  "Checking E-E-A-T: experience, expertise, authority, and trust signals.",
  "Scanning for schema markup and structured data coverage.",
  "Analyzing semantic depth and topic cluster completeness.",
  "Composing the AI SEO audit report.",
];

/* --------------------------------------------------------------------------
   Platform icons
   -------------------------------------------------------------------------- */

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function ChatGPTIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 41 41" fill="none" aria-hidden="true">
      <path fill="#10a37f" d="M37.532 16.87a9.963 9.963 0 0 0-.856-8.184 10.078 10.078 0 0 0-10.855-4.835 9.964 9.964 0 0 0-6.631-2.881 10.079 10.079 0 0 0-9.61 6.971 9.967 9.967 0 0 0-6.664 4.834 10.08 10.08 0 0 0 1.24 11.817 9.965 9.965 0 0 0 .856 8.185 10.079 10.079 0 0 0 10.855 4.835 9.965 9.965 0 0 0 6.631 2.88 10.079 10.079 0 0 0 9.617-6.971 9.967 9.967 0 0 0 6.663-4.834 10.079 10.079 0 0 0-1.243-11.817zm-17.297 24.534a7.46 7.46 0 0 1-4.786-1.727c.061-.033.168-.091.237-.134l7.964-4.6a1.294 1.294 0 0 0 .655-1.134V19.054l3.366 1.944a.12.12 0 0 1 .066.092v9.299a7.505 7.505 0 0 1-7.502 7.015zm-16.134-6.892a7.471 7.471 0 0 1-.892-5.023c.06.036.162.099.237.141l7.964 4.6a1.297 1.297 0 0 0 1.308 0l9.724-5.614v3.888a.12.12 0 0 1-.048.103l-8.051 4.649a7.504 7.504 0 0 1-10.242-2.744zm-2.09-17.498a7.463 7.463 0 0 1 3.897-3.286c0 .068-.004.19-.004.274v9.201a1.294 1.294 0 0 0 .654 1.132l9.723 5.614-3.366 1.944a.12.12 0 0 1-.114.012L8.097 27.828a7.504 7.504 0 0 1-6.086-10.814zm27.658 6.437l-9.724-5.615 3.367-1.943a.121.121 0 0 1 .114-.012l8.048 4.648a7.498 7.498 0 0 1-1.158 13.528v-9.476a1.293 1.293 0 0 0-.647-1.13zm3.35-5.043c-.059-.037-.162-.099-.236-.141l-7.965-4.6a1.298 1.298 0 0 0-1.308 0l-9.723 5.614v-3.888a.12.12 0 0 1 .048-.103l8.05-4.645a7.497 7.497 0 0 1 11.135 7.763zm-21.063 6.929l-3.367-1.944a.12.12 0 0 1-.065-.092v-9.299a7.497 7.497 0 0 1 12.293-5.756 6.94 6.94 0 0 0-.236.134l-7.965 4.6a1.294 1.294 0 0 0-.654 1.132l-.006 11.225zm1.829-3.943l4.33-2.501 4.332 2.497v4.999l-4.331 2.5-4.331-2.5V21.4z"/>
    </svg>
  );
}

function PerplexityIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2L18 7.5V12L12 16.5L6 12V7.5L12 2Z" stroke="#1C9EBD" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M12 16.5V22M6 12V17.5L12 22L18 17.5V12" stroke="#1C9EBD" strokeWidth="1.5" strokeLinejoin="round"/>
      <line x1="6" y1="7.5" x2="18" y2="7.5" stroke="#1C9EBD" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="6" y1="12" x2="18" y2="12" stroke="#1C9EBD" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="12" y1="2" x2="12" y2="7.5" stroke="#1C9EBD" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function CopilotIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="cg1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0078D4"/>
          <stop offset="100%" stopColor="#7B2FBE"/>
        </linearGradient>
        <linearGradient id="cg2" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7B2FBE"/>
          <stop offset="100%" stopColor="#00BCF2"/>
        </linearGradient>
        <linearGradient id="cg3" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#00BCF2"/>
          <stop offset="100%" stopColor="#00B294"/>
        </linearGradient>
      </defs>
      <path d="M12 3C7.03 3 3 7.03 3 12" stroke="url(#cg1)" strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M3 12C3 16.97 7.03 21 12 21" stroke="url(#cg2)" strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M12 21C16.97 21 21 16.97 21 12" stroke="url(#cg3)" strokeWidth="2.2" strokeLinecap="round"/>
      <path d="M21 12C21 9.2 19.6 6.7 17.5 5.1" stroke="url(#cg1)" strokeWidth="2.2" strokeLinecap="round"/>
      <circle cx="12" cy="12" r="2.5" fill="#0078D4"/>
    </svg>
  );
}

/* --------------------------------------------------------------------------
   Email HTML — deep navy + blue accent, light mode
   -------------------------------------------------------------------------- */

const EMAIL_COLORS = {
  page:         "#eef2f7",
  headerBg:     "#0f1f3d",
  headerMute:   "#64748b",
  headerBody:   "#94a3b8",
  card:         "#ffffff",
  ink:          "#111827",
  body:         "#374151",
  mute:         "#9ca3af",
  ruleSoft:     "#e5e7eb",
  accent:       "#2563eb",
  ok:           "#059669",
  okBg:         "#ecfdf5",
  okBorder:     "#a7f3d0",
  warn:         "#d97706",
  warnBg:       "#fffbeb",
  warnBorder:   "#fde68a",
  bad:          "#dc2626",
  badBg:        "#fff1f2",
  badBorder:    "#fecaca",
  topWinBg:     "#eff6ff",
  topWinAccent: "#1d4ed8",
  topWinImpact: "#16a34a",
};

function scoreColor(s) {
  if (s >= 75) return EMAIL_COLORS.ok;
  if (s >= 50) return EMAIL_COLORS.warn;
  return EMAIL_COLORS.bad;
}

function scoreLabel(s) {
  if (s >= 75) return "Strong";
  if (s >= 50) return "Fair";
  if (s >= 30) return "Weak";
  return "Critical";
}

const findingTypeConfig = {
  critical:    { icon: "⚠",  color: EMAIL_COLORS.bad,  bg: EMAIL_COLORS.badBg,  border: EMAIL_COLORS.badBorder },
  warning:     { icon: "⚠",  color: EMAIL_COLORS.warn, bg: EMAIL_COLORS.warnBg, border: EMAIL_COLORS.warnBorder },
  opportunity: { icon: "▲",  color: EMAIL_COLORS.ok,   bg: EMAIL_COLORS.okBg,   border: EMAIL_COLORS.okBorder },
};

function buildEmailHTML(results, url, recipientEmail) {
  const domain = (url || "").replace(/^https?:\/\//, "").split("/")[0] || "your site";
  const c = EMAIL_COLORS;

  const scoreRow = (label, score) => {
    const color    = scoreColor(score);
    const labelTxt = scoreLabel(score);
    const badgeBg  = score >= 75 ? c.okBg : score >= 50 ? c.warnBg : c.badBg;
    return `
      <tr>
        <td style="padding:14px 0; border-bottom:1px solid ${c.ruleSoft};">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.ink}; padding:0 8px 0 0;">${label}</td>
              <td style="text-align:right; padding:0 12px 0 0; white-space:nowrap;">
                <span style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:11px; color:${color}; background:${badgeBg}; padding:2px 8px; border-radius:999px; font-weight:600;">${labelTxt}</span>
              </td>
              <td style="font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.ink}; text-align:right; width:56px; font-variant-numeric:tabular-nums; font-weight:700; white-space:nowrap;">
                ${score}<span style="font-size:11px; color:${c.mute}; font-weight:400;">/100</span>
              </td>
            </tr>
            <tr>
              <td colspan="3" style="padding-top:8px;">
                <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                  <tr>
                    <td style="height:3px; line-height:3px; font-size:0; background:${color}; width:${score}%;">&nbsp;</td>
                    <td style="height:3px; line-height:3px; font-size:0; background:${c.ruleSoft}; width:${100 - score}%;">&nbsp;</td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>`;
  };

  const findingRow = (f) => {
    const cfg = findingTypeConfig[f.type] || findingTypeConfig.warning;
    return `
      <tr>
        <td style="padding:0 0 10px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
            <tr>
              <td style="background:${cfg.bg}; border-left:3px solid ${cfg.border}; border-radius:6px; padding:13px 16px;">
                <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; line-height:1.6; color:${c.ink};">
                  <span style="color:${cfg.color}; margin-right:6px;">${cfg.icon}</span>${f.text}
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>`;
  };

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta name="color-scheme" content="light only" />
  <meta name="supported-color-schemes" content="light" />
  <title>AI SEO Audit, ${domain}</title>
  <style>
    :root { color-scheme: light only; }
    body { color-scheme: light only; background-color: ${c.page} !important; }
  </style>
</head>
<body style="margin:0; padding:0; background:${c.page}; font-family:'Helvetica Neue', Arial, sans-serif; color-scheme:light;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:${c.page}; padding:32px 12px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" role="presentation" style="max-width:600px; width:100%; border-radius:16px; overflow:hidden; border:1px solid #e2e8f0;">

          <!-- HEADER -->
          <tr>
            <td style="background:${c.headerBg}; padding:32px 40px 0;">
              <p style="margin:0 0 20px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.headerMute};">
                YourGrowthPartner.io
              </p>
              <h1 style="margin:0 0 6px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:28px; line-height:1.1; font-weight:700; color:#ffffff;">
                Your Free AI SEO Audit
              </h1>
              <p style="margin:0 0 28px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; color:${c.headerMute};">
                ${domain}
              </p>
            </td>
          </tr>

          <!-- SCORE HERO -->
          <tr>
            <td style="background:${c.headerBg}; padding:0 40px 32px; border-bottom:2px solid #1a3560;">
              <p style="margin:0 0 6px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.headerBody};">
                Overall AI SEO score
              </p>
              <p style="margin:0 0 18px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:56px; line-height:1; font-variant-numeric:tabular-nums; font-weight:700; color:${c.accent};">
                ${results.overallScore}<span style="font-size:16px; color:${c.headerBody}; font-weight:400; margin-left:6px;">/100</span>
              </p>
              <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:15px; line-height:1.6; color:${c.headerBody};">
                ${results.summary || ""}
              </p>
            </td>
          </tr>

          <!-- SCORE BREAKDOWN -->
          <tr>
            <td style="background:${c.card}; padding:28px 40px 8px;">
              <p style="margin:0 0 16px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.mute};">
                Score breakdown
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                ${scoreRow("AI Search Visibility", results.aiVisibilityScore)}
                ${scoreRow("Traditional SEO", results.traditionalSeoScore)}
                ${scoreRow("Content Authority (E-E-A-T)", results.contentAuthorityScore)}
                ${scoreRow("Structured Data", results.structuredDataScore)}
                ${scoreRow("Topic Depth", results.topicClusterScore)}
              </table>
            </td>
          </tr>

          <!-- FINDINGS -->
          <tr>
            <td style="background:${c.card}; padding:24px 40px 20px;">
              <p style="margin:0 0 16px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.mute};">
                Key findings
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                ${(results.findings || []).map(findingRow).join("")}
              </table>
            </td>
          </tr>

          <!-- TOP WIN -->
          <tr>
            <td style="background:${c.card}; padding:0 40px 28px;">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation">
                <tr>
                  <td style="background:${c.topWinBg}; border-radius:8px; padding:20px 24px;">
                    <p style="margin:0 0 10px; font-family:'Courier New',monospace; font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:${c.topWinAccent}; font-weight:bold;">
                      &#9889; Your highest-impact AI SEO win
                    </p>
                    <p style="margin:0 0 12px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:16px; line-height:1.5; color:${c.ink}; font-weight:600;">
                      ${results.topWin || ""}
                    </p>
                    <p style="margin:0; font-family:'Helvetica Neue',Arial,sans-serif; font-size:13px; color:${c.topWinImpact}; font-weight:600;">
                      Estimated impact: ${results.estimatedTrafficImpact || ""}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="background:${c.headerBg}; padding:36px 40px 40px; text-align:center;">
              <h2 style="margin:0 0 12px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:22px; line-height:1.3; font-weight:700; color:#ffffff;">
                Ready to rank in ChatGPT,<br>Perplexity &amp; AI Overviews?
              </h2>
              <p style="margin:0 0 24px; font-family:'Helvetica Neue',Arial,sans-serif; font-size:14px; line-height:1.6; color:${c.headerBody};">
                Book a free 30-minute strategy call. We&rsquo;ll walk through your audit live and build the exact action plan to get your site surfaced by AI search engines.
              </p>
              <a href="${CALENDLY_LINK}" style="display:inline-block; font-family:'Helvetica Neue',Arial,sans-serif; font-size:15px; color:${c.headerBg}; background:#ffffff; padding:14px 32px; border-radius:999px; text-decoration:none; font-weight:700; letter-spacing:-0.005em;">
                Book My Free Strategy Call &rarr;
              </a>
            </td>
          </tr>

          <!-- FOOTER -->
          <tr>
            <td style="background:#06111f; padding:16px 40px; text-align:center;">
              <p style="margin:0; font-family:'Courier New',monospace; font-size:10px; letter-spacing:0.12em; color:#475569;">
                YourGrowthPartner.io &middot; AI SEO Audit for ${recipientEmail}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function normalizeUrl(input) {
  if (!input) return "";
  const trimmed = input.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidUrl(input) {
  try {
    const u = new URL(normalizeUrl(input));
    return /\./.test(u.hostname) && u.hostname.length > 3;
  } catch {
    return false;
  }
}

function isValidEmail(input) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

function isValidPhone(input) {
  const digits = input.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 15;
}

function getDomain(url) {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export default function AISEOAudit() {
  const [phase, setPhase] = useState("input");

  const [url,      setUrl]      = useState("");
  const [email,    setEmail]    = useState("");
  const [phone,    setPhone]    = useState("");
  const [dialCode, setDialCode] = useState("+1");

  const [touched,   setTouched]   = useState({ url: false, email: false, phone: false });
  const [errors,    setErrors]    = useState({});
  const [errorMsg,  setErrorMsg]  = useState("");
  const [scanIndex, setScanIndex] = useState(0);
  const tickerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch("https://ipapi.co/json/")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.country_calling_code) setDialCode(d.country_calling_code);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    if (tickerRef.current) clearInterval(tickerRef.current);
  }, []);

  function validate() {
    const next = {};
    if (!isValidUrl(url))     next.url   = "Enter a full domain, including the dot.";
    if (!isValidEmail(email)) next.email = "We need a real address to send the audit.";
    if (!isValidPhone(phone)) next.phone = "Six to fifteen digits.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    setTouched({ url: true, email: true, phone: true });
    if (!validate()) return;

    const finalUrl  = normalizeUrl(url);
    const fullPhone = `${dialCode}${phone.replace(/\D/g, "")}`;
    const eventId   = `lead_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    // Show booking page immediately — no waiting
    setPhase("success");
    window.fbq?.("track", "Lead", {}, { eventID: eventId });

    // Audit + email run silently in the background
    fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: finalUrl, email, phone: fullPhone, eventId }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(results => {
        if (!results) return;
        fetch("https://api.emailjs.com/api/v1.0/email/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            service_id:    EMAILJS_SERVICE_ID,
            template_id:   EMAILJS_TEMPLATE_ID,
            user_id:       EMAILJS_PUBLIC_KEY,
            template_params: {
              to_email:  email,
              subject:   `AI SEO Audit, ${getDomain(finalUrl)} (score ${results.overallScore}/100)`,
              html_body: buildEmailHTML(results, finalUrl, email),
            },
          }),
        }).catch(() => {});
      })
      .catch(() => {});
  }

  function reset() {
    if (tickerRef.current) clearInterval(tickerRef.current);
    setPhase("input");
    setUrl("");
    setEmail("");
    setPhone("");
    setTouched({ url: false, email: false, phone: false });
    setErrors({});
    setErrorMsg("");
    setScanIndex(0);
  }

  function blurField(name, validator) {
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((prev) => {
      const next  = { ...prev };
      const value = name === "url" ? url : name === "email" ? email : phone;
      if (validator(value)) delete next[name];
      else next[name] = prev[name] || "Required.";
      return next;
    });
  }

  const formInvalid = !isValidUrl(url) || !isValidEmail(email) || !isValidPhone(phone);
  const domain      = getDomain(url);

  return (
    <div className="page">
      <div className="masthead">
        <span className="wordmark">YourGrowthPartner</span>
        <span className="masthead-meta">AI SEO Audit &middot; Vol. 01</span>
      </div>

      <main>
        {phase === "input" && (
          <div className="shell phase">
            <section aria-labelledby="hero">
              <span className="tag">The audit no agency wants you to run</span>
              <h1 className="hero" id="hero">
                Your competitors are already ranking in AI tools.{" "}
                <span className="hero-highlight">Are you?</span>
              </h1>
              <p className="lede">
                You&rsquo;re not invisible by accident. Every query in your
                industry scores competing pages for authority, structure, and
                citability. Yours is losing that race right now. We&rsquo;ll
                show you exactly where and what it&rsquo;s costing you.
              </p>
              <ul className="checklist">
                <li>No agency spin. Raw scores.</li>
                <li>Any website, 60 seconds</li>
                <li>Free. No strings.</li>
              </ul>

            </section>

            <aside className="form-card" aria-labelledby="form-title">
              <h2 className="form-card-title" id="form-title">
                Which site should we audit?
              </h2>
              <p className="form-card-sub">
                URL, email, phone. The audit lands in your inbox, the strategy call is up to you.
              </p>

              <form
                className="form"
                onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                noValidate
              >
                <div className="field">
                  <label className="field-label" htmlFor="url-input">Website URL</label>
                  <input
                    id="url-input"
                    className={`field-input ${touched.url && errors.url ? "is-invalid" : ""}`}
                    type="url"
                    inputMode="url"
                    autoComplete="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onBlur={() => blurField("url", isValidUrl)}
                    placeholder="yourdomain.com"
                    aria-invalid={touched.url && Boolean(errors.url)}
                    aria-describedby={errors.url ? "url-error" : undefined}
                    required
                  />
                  {touched.url && errors.url && (
                    <p className="field-error" id="url-error" role="alert">{errors.url}</p>
                  )}
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="email-input">Email</label>
                  <input
                    id="email-input"
                    className={`field-input ${touched.email && errors.email ? "is-invalid" : ""}`}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => blurField("email", isValidEmail)}
                    placeholder="you@company.com"
                    aria-invalid={touched.email && Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    required
                  />
                  {touched.email && errors.email && (
                    <p className="field-error" id="email-error" role="alert">{errors.email}</p>
                  )}
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="phone-input">Phone</label>
                  <div className="field-phone">
                    <span className="dialcode" aria-label="Country dial code">{dialCode}</span>
                    <input
                      id="phone-input"
                      className={`field-input ${touched.phone && errors.phone ? "is-invalid" : ""}`}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onBlur={() => blurField("phone", isValidPhone)}
                      placeholder="555 0100"
                      aria-invalid={touched.phone && Boolean(errors.phone)}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                      required
                    />
                  </div>
                  {touched.phone && errors.phone && (
                    <p className="field-error" id="phone-error" role="alert">{errors.phone}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="cta"
                  disabled={formInvalid}
                  aria-disabled={formInvalid}
                >
                  Run the AI SEO audit
                  <span className="cta-glyph" aria-hidden="true">&rarr;</span>
                </button>

                <p className="fineprint">
                  Free. Sixty seconds. No card.
                </p>

                <p className="call-nudge-inline">
                  Already know you need help?{" "}
                  <a href={CALENDLY_LINK} target="_blank" rel="noopener noreferrer">
                    Skip the audit. Book a strategy call directly &rarr;
                  </a>
                </p>
              </form>
            </aside>

            <div className="platform-strip">
              <p className="platform-label">Audits your ranking in</p>
              <div className="platform-logos">
                <div className="platform-item"><GoogleIcon /><span>Google AI</span></div>
                <div className="platform-item"><ChatGPTIcon /><span>ChatGPT</span></div>
                <div className="platform-item"><PerplexityIcon /><span>Perplexity</span></div>
                <div className="platform-item"><CopilotIcon /><span>Copilot</span></div>
              </div>
            </div>
          </div>
        )}

        {phase === "success" && (
          <section className="phase" aria-labelledby="confirm">
            <span className="tag">Audit running</span>
            <h2 className="hero" id="confirm">
              We&rsquo;re auditing {domain || "your site"} now.
            </h2>
            <p className="lede">
              Your full AI SEO report will land in <strong>{email}</strong> within
              a few minutes. While we work, book your free strategy call below.
              We&rsquo;ll walk through the findings live and build the exact fix plan.
            </p>

            <div className="pullquote">
              <p className="pullquote-eyebrow">Don&rsquo;t wait for the email</p>
              <p className="pullquote-line">
                Book a free 30-minute call now. We&rsquo;ll go over your full audit
                live, tell you exactly why AI search isn&rsquo;t ranking you, and give
                you a step-by-step plan to fix it.
              </p>
              <a
                className="cta"
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: "auto", display: "inline-flex" }}
              >
                Book my free strategy call
                <span className="cta-glyph" aria-hidden="true">&rarr;</span>
              </a>
            </div>

            <button className="secondary" type="button" onClick={reset}>
              Run another audit
            </button>
          </section>
        )}

        {phase === "error" && (
          <section className="phase error-block" aria-labelledby="err">
            <span className="tag" style={{ background: "oklch(86% 0.040 22)" }}>Audit failed</span>
            <h2 className="hero" id="err">
              Something didn&apos;t complete.
            </h2>
            <p className="lede">{errorMsg}</p>
            <button className="cta" type="button" onClick={reset} style={{ width: "auto", display: "inline-flex" }}>
              Try again
              <span className="cta-glyph" aria-hidden="true">&rarr;</span>
            </button>
          </section>
        )}
      </main>

      <footer className="colophon">
        <p>
          YourGrowthPartner.io. AI SEO Audit. E-E-A-T analysis, structured data audit, and AI search visibility scoring.
        </p>
      </footer>
    </div>
  );
}
